import { keyBy, merge, values } from 'lodash';
import { computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useWesley } from '@anthill-modules/wesley-components';
import { useI18n } from 'vue-i18n';

import {
  SetCurrentDesignSystemDocumentElements,
  SetCurrentDsConfig,
  SetEmailTokens,
  SetSelectedObjectDocumentEditor,
} from '~/constants/actions';
import { LAYOUT_CHANNEL, RESPONSE_TYPE } from '~/constants/conversationModels';
import { DOCUMENT_ACTIONS, EDITOR_ACTIONS } from '~/constants/documentActions';
import {
  GetAllTemplates,
  GetCurrentConversationName,
  GetCurrentDesignSystemDocumentElements,
  GetCurrentLayout,
  GetCurrentTemplate,
  GetDsConfig,
  GetEmailTokens,
  GetSelectedObjectDocumentEditor,
} from '~/constants/getters';

import { SetAllTemplates, SetAvailableLayouts, SetCurrentLayout, SetCurrentTemplate } from '~/constants/mutations';

import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import createHtmlHistory from '~/composables/createHtmlHistory';
import useDocuments from '~/composables/useDocuments';
import useAuthoring from '~/composables/useAuthoring';
import { getSnippetData, refreshReferencesData } from '~/utils/footnotes';
import { createLayout, isImage, isImageUrl, mapEditorIdsToLLMIds } from '~/utils/editor-utils';
import { removeCitationBulletsOverlay, renderCitationBulletsOverIframe } from '~/utils/citations-utils';

export default () => {
  const store = useStore();
  const route = useRoute();
  const { t } = useI18n();
  const { getAuthoredDocumentImportedThumbs, getDocumentThumbs, openDocumentFromTitle } = useDocuments();
  const { getDocumentContent, saveEditorContentStateFromIframe } = createHtmlHistory();
  const {
    sendMessage: sendMessageToDocument,
    asyncSendMessage: sendAsyncMessageToDocument,
    iframeWindow,
  } = useWesley();
  const { footnotes, setFootnotes, displayReferencesOnDocumentLevel } = useAuthoring();

  const arcaneApiGateway = inject('arcaneApiGateway');
  const realtime = inject('realtime');

  let waitingForMessage = false;

  const currentTemplates = computed(() => store.getters[`documentStore/${GetAllTemplates}`]);
  const currentTemplate = computed(() => store.getters[`documentStore/${GetCurrentTemplate}`]);
  const currentDesignSystemDocumentElements = computed(
    () => store.getters[`documentStore/${GetCurrentDesignSystemDocumentElements}`],
  );
  const currentDsConfig = computed(() => store.getters[`documentStore/${GetDsConfig}`]);
  const emailTokens = computed(() => store.getters[`documentStore/${GetEmailTokens}`]);
  const currentLayout = computed(() => store.getters[`documentStore/${GetCurrentLayout}`]);
  const selectedObjectDocumentEditor = computed(
    () => store.getters[`documentStore/${GetSelectedObjectDocumentEditor}`],
  );

  const currentConversationName = computed(() => store.getters[`conversationStore/${GetCurrentConversationName}`]);

  const setCurrentTemplate = (data) => store.dispatch(`documentStore/${SetCurrentTemplate}`, data);

  const setSelectedObjectDocumentEditor = (data) =>
    store.dispatch(`documentStore/${SetSelectedObjectDocumentEditor}`, data);

  const setCurrentDesignSystemDocumentElements = (data) =>
    store.dispatch(`documentStore/${SetCurrentDesignSystemDocumentElements}`, data);

  const getAllTemplates = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.templates);
      await store.dispatch(`documentStore/${SetAllTemplates}`, data);
      return data;
    } catch (error) {
      console.log('Failed to load user design systems', error);
      return [];
    }
  };

  const getTemplateById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.getTemplateById(id));
      if (data.length) {
        await store.dispatch(`documentStore/${SetCurrentTemplate}`, data[0]);
        return data[0];
      }
      return data;
    } catch (error) {
      console.log('Failed to load templates', error);
      return [];
    }
  };

  const getDesignSystemById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.getDesignSystemById(id));
      if (data.length) {
        const { config = {} } = data[0];
        await store.dispatch(`documentStore/${SetCurrentDsConfig}`, config);
        return data[0];
      }
      return data;
    } catch (error) {
      console.log('Failed to load design system', error);
      return [];
    }
  };

  const getEmailTokens = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.editorConfig.getEmailTokensByOrg);
      if (data.length) {
        await store.dispatch(`documentStore/${SetEmailTokens}`, data);
        return data;
      }
      return data;
    } catch (error) {
      console.log('Failed to load email tokens', error);
      return [];
    }
  };

  const getAvailableLayouts = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.layouts);
      await store.dispatch(`documentStore/${SetAvailableLayouts}`, data);

      return data;
    } catch (error) {
      console.log('Failed to load layouts', error);
      return [];
    }
  };

  const getLayoutsByParentId = async (designSystemId, needThumbs = false) => {
    try {
      let { data: currentLayouts } = await arcaneApiGateway.get(
        apiGatewayEndpoints.designSystems.getLayoutsByParentId(designSystemId),
      );
      if (needThumbs) {
        const layoutIds = currentLayouts.map(({ id }) => id);
        const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.designSystems.layoutsThumbs, {
          layoutIds,
          designSystemId,
        });
        currentLayouts = values(merge(keyBy(data, 'id'), keyBy(currentLayouts, 'id')));
      }

      return currentLayouts;
    } catch (error) {
      console.log('Failed to load layouts', error);
      return [];
    }
  };

  const getLayoutById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.designSystems.getLayoutById(id));

      if (data.length) {
        await store.dispatch(`documentStore/${SetCurrentLayout}`, data[0]);
        return data[0];
      }

      return data;
    } catch (error) {
      console.log('Failed to load layout', error);
      return [];
    }
  };

  const mapAuthoringLayoutDataToSend = () => {
    const selectedLayout = selectedObjectDocumentEditor.value.selectedLayout;
    const selectedElement = selectedObjectDocumentEditor.value.selectedElement;
    const layouts =
      (currentDesignSystemDocumentElements.value || []).map((layout) =>
        createLayout(layout, EDITOR_ACTIONS.selectDocument),
      ) || [];

    // when we select a specific element from layout
    if (selectedLayout?.cssPath && selectedElement?.cssPath) {
      return { layouts, selected_layout: createLayout(selectedLayout, EDITOR_ACTIONS.selectElement, selectedElement) };
    } else if (selectedLayout?.children?.length) {
      // when we select one layout
      return { layouts, selected_layout: createLayout(selectedLayout, EDITOR_ACTIONS.selectLayout) };
    } else {
      return { layouts };
    }
  };

  const handleDocumentResponseEvent = (data) => {
    switch (data.method) {
      case 'getSelectableElementsData':
        setCurrentDesignSystemDocumentElements(data.response);
        break;

      case 'batch':
        (data.response || []).forEach((single) => handleDocumentResponseEvent(single));
        break;

      default:
        // console.log('Unhandled Web Editor we:response event');
        break;
    }
  };
  let callbackEnded = false;
  let displayCitationBullets = true;

  const layoutCallback = async (message) => {
    const data = JSON.parse(message.data);
    const { correlationId, responseType } = data;

    console.log('Data received-->', responseType, data.data);

    switch (responseType) {
      case RESPONSE_TYPE.layoutGeneration: {
        displayCitationBullets = false;

        // render for displayCitationBullets = false means we keep hidding references while filling document
        renderCitationTooltipsInIframe();

        await enqueueElementForSettingDocumentContent({ type: responseType, data: data?.data?.[0] });
        break;
      }
      case RESPONSE_TYPE.endMessageLayoutGeneration: {
        await enqueueElementForSettingDocumentContent({ type: responseType });
        break;
      }
      case RESPONSE_TYPE.data: {
        await enqueueElementForSettingDocumentContent({ type: responseType, data: data?.data?.[0] });
        break;
      }

      default:
        console.log('[ERROR]', data);
        console.log('Invalid type of message');
    }

    if (waitingForMessage) {
      await postLog(correlationId, `Received message: ${JSON.stringify(data)}`);
      waitingForMessage = false;
    }
  };

  const updateReferencesListWithThumbs = async (references, withSaveData = false) => {
    const currentFootnotes = footnotes.value || [];
    const referencesAddedByUser = currentFootnotes.filter((footnote) => footnote.addedByUser);

    // Merge and deduplicate references
    const uniqueFootnotes = Array.from(
      new Map(
        [...referencesAddedByUser, ...currentFootnotes, ...references].map((footnote) => [
          footnote.id || footnote.externalId,
          { ...footnote },
        ]),
      ).values(),
    );

    const documents = uniqueFootnotes.reduce((acc, { externalId, pageNumber, addedByUser }) => {
      if (!addedByUser) {
        acc.push({ id: externalId, pages: pageNumber });
      }
      return acc;
    }, []);

    const response = await getDocumentThumbs(documents);

    // Create a map of API response data by documentId
    const responseMap = keyBy(response, 'documentId');

    // Merge footnotes with API response data, preserving footnotes with addedByUser: true
    const updatedFootnotes = uniqueFootnotes.map((footnote) => {
      const externalId = footnote.externalId || footnote.id;
      const apiData = responseMap[externalId];

      if (footnote.addedByUser) {
        // Keep footnotes added by user as-is
        return footnote;
      } else if (apiData) {
        // Merge API data with existing footnote data
        return { ...footnote, ...apiData };
      } else {
        // Keep footnote as-is if no API data found
        return footnote;
      }
    });

    setFootnotes(updatedFootnotes);
    if (withSaveData) {
      await onHandleCitationsAndSaveDocument();
    }
  };

  const handleFieldVisibility = async (field) => {
    const elementAttributes = await sendAsyncMessageToDocument({
      method: 'getAttributes',
      params: {
        query: field.field_id,
      },
    });

    const hidden = elementAttributes?.data?.response?.hidden;
    if (field.visible === hidden) {
      sendMessageToDocument({
        method: 'updateAttribute',
        params: {
          query: field.field_id,
          attr: 'hidden',
          value: field.visible,
        },
      });
    }
  };

  const fillCurrentField = async (field) => {
    if (isImage({ fieldFromLLM: field }) && isImageUrl(field.value)) {
      try {
        const images = await getAuthoredDocumentImportedThumbs([field.value]);
        if (images?.length) {
          sendMessageToDocument({
            method: 'updateAttribute',
            params: {
              query: field.field_id,
              value: images?.[0].url,
              attr: 'src',
            },
          });
          saveEditorContentStateFromIframe();
        } else {
          console.log('Images not found');
        }
      } catch (error) {
        console.log('Error on getting images', error);
      }
    } else {
      const message = field.value?.replace(
        /\[([^\]]+?)-(\d+),\s*p\.(\d+)\]/g,
        (match, id, snippetIndex, page) =>
          `<span class="citation-span" data-doc-id="${id}" data-index="${snippetIndex}" data-page="${page}" ></span>`,
      );
      sendMessageToDocument({
        method: 'setContent',
        params: {
          query: field.field_id,
          content: message,
          select: false,
        },
      });
    }
    await handleFieldVisibility(field);
  };

  const mapCitationsDataFromFootnote = async (id, page, snippet, snippetIndex) => {
    const bulletData = footnotes.value.find((ref) => (ref.externalId || ref.id) === id);
    // this means the reference was deleted
    if (!bulletData) {
      return null;
    }

    let pageData = page;
    let name = 'Document Reference';
    let snippetData = snippet;
    const thumbnailUrl = bulletData.pages ? bulletData.pages[page]?.small || '' : '';
    const isCustomReference = bulletData && bulletData.addedByUser;

    if (bulletData) {
      snippetData = getSnippetData({
        snippets: bulletData.snippets,
        page: pageData,
        snippetIndex,
      });
      name = bulletData.name;
    }

    page = Number(pageData); //TODO: handle case there are multiple pages

    return { thumbnailUrl, page, name, snippet: snippetData, isCustomReference };
  };

  const refreshReferencesOnUpdate = () => {
    const iframeBody = iframeWindow.value?.document?.body;

    if (iframeBody) {
      return refreshReferencesData({
        htmlString: iframeBody.innerHTML,
        currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
        footnotes: footnotes.value,
        sendMessageToDocument,
        sendAsyncMessageToDocument,
        t,
        updateEditorSelectableElementsData,
        displayReferencesOnDocumentLevel: displayReferencesOnDocumentLevel.value,
        saveEditorContentStateFromIframe,
      });
    }
  };

  const renderCitationTooltipsInIframe = () => {
    if (!displayCitationBullets) {
      removeCitationBulletsOverlay({ iframeDoc: iframeWindow.value?.document });
      return;
    }

    renderCitationBulletsOverIframe({
      iframeDoc: iframeWindow.value?.document,
      openDocument: openDocumentFromTitle,
      mapCitationsDataFromFootnote,
      saveEditorContentStateFromIframe,
      refreshReferencesOnDelete: refreshReferencesOnUpdate,
      updateEditorSelectableElementsData,
    });
    sendMessageToDocument({ method: 'getSelectableElementsData' });
  };

  let layoutQueue = [];
  let isProcessing = false;
  let currentIndex = -1;
  let fieldsIdsMapping = {};
  let currentLayoutId = '';

  const resetLayoutIndex = async () => {
    layoutQueue = [];
    currentIndex = -1;
    fieldsIdsMapping = {};
    isProcessing = false;
    // ensure editor is fully destroyed before  continuing updating the content from LLM
    const iframe = iframeWindow.value;
    if (iframe && iframe.document) {
      sendMessageToDocument({ method: 'editorDestroy' });
    }
  };

  const updateAuthoredDocument = async (id, data) => {
    await arcaneApiGateway.put(apiGatewayEndpoints.authoredDocuments.createOrUpdateAuthoredDocument(id), data);
  };

  const updateAuthoredDocumentResource = async (id, data, useKeepAlive) => {
    try {
      saveEditorContentStateFromIframe(true);
      if (useKeepAlive) {
        sessionStorage.clear();
        await arcaneApiGateway.putKeepAlive(
          apiGatewayEndpoints.authoredDocuments.createOrUpdateAuthoredDocument(id),
          data,
        );
      } else {
        await updateAuthoredDocument(id, data);
      }
    } catch (error) {
      console.log('Failed to save Authored Document', error);
    }
  };

  const saveHtmlDocumentContentToDb = async (useKeepAlive = false) => {
    const content = await getDocumentContent();
    const currentConversationId = route.params.conversationId;

    if (currentConversationId && content) {
      await updateAuthoredDocumentResource(
        currentConversationId,
        { title: currentConversationName.value, content, footnotes: footnotes.value },
        useKeepAlive,
      );
    }
  };

  const handleElementWithAction = async (layout) => {
    let newLayout;
    let shouldUpdateTemplate = false;

    if (layout.id !== currentLayoutId) {
      // check if I'm moving to the next layout
      currentLayoutId = layout.id;
      currentIndex++;

      if (layout.newLayoutId) {
        // if action: create or update I should receive newLayoutId
        newLayout = await getLayoutById(layout.newLayoutId);
        if (newLayout && newLayout.content) {
          shouldUpdateTemplate = true;
        } else {
          console.log('New layout not found or content missed');
        }
      }

      updateEditorSelectableElementsData();
      saveEditorContentStateFromIframe();
    }

    switch (layout.action) {
      case DOCUMENT_ACTIONS.create:
        try {
          if (shouldUpdateTemplate) {
            const response = await sendAsyncMessageToDocument({
              method: currentIndex > 0 ? 'insertAfter' : 'insertBefore',
              params: {
                query: currentDesignSystemDocumentElements.value[currentIndex - 1].cssPath,
                element: newLayout.content,
              },
            });
            await sendAsyncMessageToDocument({ method: 'getSelectableElementsData' });
            fieldsIdsMapping = response?.data?.response?.updatedIDs || {};
            setTimeout(async () => {
              renderCitationTooltipsInIframe();
            }, 200);
          }
          await updateLayoutCssPathBasedOnTemplateAndFill(layout);
        } catch (err) {
          console.log('Error on adding new layout', err);
        }
        break;
      case DOCUMENT_ACTIONS.update:
        try {
          if (shouldUpdateTemplate) {
            const response = await sendAsyncMessageToDocument({
              method: 'insertAfter',
              params: {
                query: layout.id,
                element: newLayout.content,
                select: false,
              },
            });

            fieldsIdsMapping = response?.data?.response?.updatedIDs || {};

            await sendAsyncMessageToDocument({
              method: 'removeElement',
              params: {
                query: layout.id,
              },
            });

            await sendAsyncMessageToDocument({ method: 'getSelectableElementsData' });

            setTimeout(async () => {
              renderCitationTooltipsInIframe();
            }, 200);
          }
          await updateLayoutCssPathBasedOnTemplateAndFill(layout);
        } catch (err) {
          console.log('Error on updating layout', err);
        }
        break;
      case DOCUMENT_ACTIONS.delete:
        //this check is needed because update action sends data for each layout
        if (layout.field.field_index === 0) {
          await sendAsyncMessageToDocument({
            method: 'removeElement',
            params: {
              query: layout.id,
            },
          });
          await sendAsyncMessageToDocument({ method: 'getSelectableElementsData' });
          currentIndex--;
          setTimeout(async () => {
            renderCitationTooltipsInIframe();
          }, 200);
        }
        break;
      default:
        await fillCurrentField(layout.field);
        break;
    }
  };

  // because cssPath on our template looks different having parent and all needed data in document context, it will look different from the one received from BE and LLM
  // ==> we have to update id with correct cssPath in order to be able to call setContent function
  const updateLayoutCssPathBasedOnTemplateAndFill = async (layout) => {
    const updatedField = {
      ...layout.field,
      field_id: mapEditorIdsToLLMIds(layout.field.field_id, fieldsIdsMapping),
    };

    await fillCurrentField(updatedField);
  };

  const onEndContentGeneration = () => {
    displayCitationBullets = true;
    callbackEnded = true;
    currentIndex = -1;
    fieldsIdsMapping = {};
    isProcessing = false;
    layoutQueue = [];
  };

  const onHandleCitationsAndSaveDocument = async () => {
    renderCitationTooltipsInIframe();
    refreshReferencesOnUpdate();
    // save state for undo redo
    saveEditorContentStateFromIframe();
    setTimeout(async () => {
      await saveHtmlDocumentContentToDb();
    }, 2500);
  };

  const enqueueElementForSettingDocumentContent = async (data) => {
    layoutQueue.push(data);

    if (isProcessing) {
      return;
    }

    isProcessing = true; // lock
    try {
      await processQueue();
    } finally {
      isProcessing = false; // always unlock, even if errors
    }
  };

  const processQueue = async () => {
    while (layoutQueue.length > 0) {
      const current = layoutQueue.shift();
      if (!current) continue;

      const { type: responseType, data } = current;

      try {
        switch (responseType) {
          case RESPONSE_TYPE.layoutGeneration: {
            const el = data;

            if (el?.locked) {
              if (el.field?.field_index === 0) currentIndex++;
              // Skip this item; continue with next without recursion
              continue;
            }

            if (el !== undefined) {
              await handleElementWithAction(el);
            }
            // then continue loop
            break;
          }

          case RESPONSE_TYPE.endMessageLayoutGeneration: {
            break;
          }

          case RESPONSE_TYPE.data: {
            const references = data?.references || [];
            if (references.length) {
              await updateReferencesListWithThumbs(references, true);
            } else {
              await onHandleCitationsAndSaveDocument();
            }
            onEndContentGeneration();
            break;
          }

          default:
            break;
        }
      } catch (err) {
        console.error('[Queue Error]', err);
        continue;
      }
    }

    // drained
    if (callbackEnded) {
      callbackEnded = false;
    }
  };
  const postLog = async (correlationId, message) =>
    arcaneApiGateway.post(apiGatewayEndpoints.logger.putLog, {
      correlationId,
      message,
      entity: 'Frontend',
      timestamp: Date.now(),
    });

  const subscribeLayoutGeneration = (conversationId) => {
    const channelName = `${LAYOUT_CHANNEL.layout_generation}:${conversationId}`;

    realtime.subscribeToUserChannel({
      channel: channelName,
      topic: '',
      cb: layoutCallback,
    });
  };

  const unsubscribeLayoutGeneration = (conversationId) => {
    layoutQueue = [];
    const channelName = `${LAYOUT_CHANNEL.layout_generation}:${conversationId}`;

    realtime.unsubscribeFromUserChannel({
      channel: channelName,
      topic: '',
    });
  };

  const updateEditorSelectableElementsData = () => {
    sendMessageToDocument({ method: 'getSelectableElementsData' });
    setTimeout(() => {
      renderCitationTooltipsInIframe();
    }, 200);
  };

  return {
    updateAuthoredDocumentResource,
    currentTemplate,
    currentTemplates,
    currentDsConfig,
    emailTokens,
    getAllTemplates,
    getTemplateById,
    setSelectedObjectDocumentEditor,
    selectedObjectDocumentEditor,
    sendMessageToDocument,
    subscribeLayoutGeneration,
    unsubscribeLayoutGeneration,
    setCurrentDesignSystemDocumentElements,
    currentDesignSystemDocumentElements,
    handleDocumentResponseEvent,
    getAvailableLayouts,
    currentLayout,
    getLayoutById,
    resetLayoutIndex,
    setCurrentTemplate,
    saveHtmlDocumentContentToDb,
    mapAuthoringLayoutDataToSend,
    getLayoutsByParentId,
    getDesignSystemById,
    getEmailTokens,
    renderCitationTooltipsInIframe,
    updateEditorSelectableElementsData,
    updateAuthoredDocument,
    updateReferencesListWithThumbs,
    refreshReferencesOnUpdate,
  };
};
